import { Stack, useTheme } from '@mui/material';
import { useMemo } from 'react';
import { DynamicIcon, SidePanelCard } from 'src/components';
import { DeletedRecordAlert } from 'src/components/DeletedRecordAlert';
import { getEntityCardIconSx, getSidePanelCardsSx } from 'src/components/EntitySidePanel';
import { BlockContainer } from 'src/components/Layout/named-layout-block.view';
import { resolvePartnerTabContactInfo } from 'src/components/SidePanelTabs/PartnerTab/partner-contact-info';
import { PartnerInfo } from 'src/components/SidePanelTabs/PartnerTab/partner-info';
import { PartnerInfoTabs } from 'src/components/SidePanelTabs/PartnerTab/partner-info-tabs';
import { useProjectQuickActions } from 'src/components/SidePanelTabs/Projects/use-project-quick-actions';
import { Project, useGroupRules, useRestoreProject, UserGroupRules } from 'src/graphql';
import { PartnerCard } from 'src/pages/Conversations/SidePanel/partner-card';
import { ProjectCard } from 'src/pages/Projects/project-card';
import { useGlobalStore } from 'src/store';
import { useScreenDimensions } from 'src/utils/system';

interface ProjectSidePanelProps {
  project: Project;
  // hosts now own the panel chrome (and its close control) directly; kept for prop-contract compatibility

  onClose?: () => void;
  extendedView?: boolean;
  /** No personalization surface exists for projects, so nothing portals into this — kept for prop-contract compatibility with the other entity panels. */
  // eslint-disable-next-line react/no-unused-prop-types
  toolsSlot?: HTMLElement | null;
}

export const ProjectSidePanel = ({
  extendedView,
  project,
  onClose,
}: ProjectSidePanelProps) => {
  const theme = useTheme();
  const { isMobile } = useScreenDimensions();
  const isMultiTenancyMode = useGlobalStore.use.isMultiTenancyMode();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { groupRules, checkValidity } = useGroupRules();
  const { restoreProject } = useRestoreProject();
  const canAccessToPartnerTab = !!groupRules[UserGroupRules.AccessToPartnerTab];
  const canSeePartnersTab =
    canAccessToPartnerTab || (isMultiTenancyMode && selectedPartnerId === null);

  const partnerWithBrandGroupContactInfo = useMemo(
    () => resolvePartnerTabContactInfo(project.partner),
    [project.partner],
  );

  useProjectQuickActions(project);

  const title = project.name || project.address?.fullAddress;

  return (
    <BlockContainer
      // the cards inside carry the only frame, so the column itself stays bare
      border="none !important"
      sx={{
        height: 'auto',
        // the collapsing wrapper sets nowrap for its width animation; undo it here or the content outgrows the column
        whiteSpace: 'normal',
      }}
    >
      <Stack
        data-testid="project-side-panel-cards"
        gap={1}
        sx={getSidePanelCardsSx({ hasFloatingTools: false, extendedView, isMobile })}
      >
        {canSeePartnersTab && project.partner ? (
          <PartnerCard partner={project.partner} data-testid="project-partner-card">
            <Stack spacing={2}>
              <PartnerInfo partner={partnerWithBrandGroupContactInfo} />
              {partnerWithBrandGroupContactInfo ? (
                <PartnerInfoTabs
                  partner={partnerWithBrandGroupContactInfo}
                  address={project.address}
                />
              ) : null}
            </Stack>
          </PartnerCard>
        ) : null}
        {project.isDeleted ? (
          <DeletedRecordAlert
            entityLabel="project"
            deletedAt={project.deletedAt}
            canRestore={checkValidity([UserGroupRules.AccessToRestoreDeletedProjects])}
            onRestore={() => restoreProject(project.id)}
          />
        ) : null}
        <SidePanelCard
          data-testid="project-details-card"
          defaultOpen
          leading={
            <DynamicIcon
              name="folder-open"
              library="lucide"
              sx={getEntityCardIconSx(theme.palette.custom.OV.entity.project)}
            />
          }
          title={title}
          titleTooltip={title ?? undefined}
        >
          <ProjectCard project={project} variant="scrollable" onDelete={onClose} />
        </SidePanelCard>
      </Stack>
    </BlockContainer>
  );
};
